import React, { useState, useEffect } from 'react';
import { getAccessToken } from '../auth/tokenService';
import { PROCESS_LABELS, PURPOSES, PURPOSE_LABELS, processesForPurpose } from '../constants/scenarioConstants';
import './UserManagement.css';

const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/admin`
  : '/api/admin';

function getHeaders() {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

// Process options for a purpose, guaranteeing an existing value stays visible
// (legacy data whose process is not in the purpose list is still shown).
function processOptions(purpose, current) {
  const list = [...processesForPurpose(purpose)];
  if (current && !list.includes(current)) list.push(current);
  return list;
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Add user form
  const [newEmail, setNewEmail] = useState('');
  const [newAccessStatus, setNewAccessStatus] = useState('active');
  const [newProcess, setNewProcess] = useState('');
  const [newPurpose, setNewPurpose] = useState('');

  // Edit user form
  const [editAccessStatus, setEditAccessStatus] = useState('active');
  const [editProcess, setEditProcess] = useState('');
  const [editPurpose, setEditPurpose] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/candidates`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.candidates || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Changing the add purpose drops a process no longer valid for it.
  const handleNewPurpose = (purpose) => {
    setNewPurpose(purpose);
    if (newProcess && !processesForPurpose(purpose).includes(newProcess)) setNewProcess('');
  };

  const handleEditPurpose = (purpose) => {
    setEditPurpose(purpose);
    if (editProcess && !processOptions(purpose, editProcess).includes(editProcess)) setEditProcess('');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!newPurpose) { setError('Please select a purpose'); return; }
    if (!newProcess) { setError('Please select a process'); return; }
    try {
      const response = await fetch(`${API_BASE}/users`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          username: newEmail.split('@')[0],
          full_name: newEmail.split('@')[0],
          email: newEmail,
          role: 'User',
          access_status: newAccessStatus,
          process: newProcess,
          purpose: newPurpose,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add user');
      }
      setMessage('Candidate added successfully. One-time password sent via email.');
      setShowModal(false);
      setNewEmail('');
      setNewAccessStatus('active');
      setNewProcess('');
      setNewPurpose('');
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveUser = async (email) => {
    if (!window.confirm(`Remove candidate "${email}"?`)) return;
    try {
      const response = await fetch(`${API_BASE}/candidates/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (response.ok) {
        setMessage(`Candidate "${email}" removed`);
        loadUsers();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setEditAccessStatus(user.access_status || 'active');
    setEditPurpose((user.purpose || '').toLowerCase());
    setEditProcess((user.process || '').toLowerCase());
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!editPurpose) { setError('Please select a purpose'); return; }
    if (!editProcess) { setError('Please select a process'); return; }
    try {
      const response = await fetch(`${API_BASE}/users/${encodeURIComponent(editUser.email)}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          access_status: editAccessStatus,
          process: editProcess,
          purpose: editPurpose,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update candidate');
      }
      setMessage(`Candidate "${editUser.email}" updated`);
      setShowEditModal(false);
      setEditUser(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="um-page">
      <main className="um-content">
        <div className="um-header-row">
          <h2 className="page-title">Candidate Management</h2>
          <button className="btn-add-user" onClick={() => setShowModal(true)}>+ Add Candidate</button>
        </div>

        {message && <div className="um-success">{message}</div>}
        {error && <div className="um-error">{error}</div>}

        <div className="um-table-wrapper">
          <table className="um-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Access Status</th>
                <th>Purpose</th>
                <th>Process</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="um-loading">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="7" className="um-empty">No candidates found. Click "+ Add Candidate" to create one.</td></tr>
              ) : (
                users.map((u, idx) => (
                  <tr key={u.email}>
                    <td>{idx + 1}</td>
                    <td>{u.email}</td>
                    <td><span className={`role-tag role-tag-${(u.access_status || 'active').toLowerCase()}`}>{u.access_status || 'active'}</span></td>
                    <td>{PURPOSE_LABELS[(u.purpose || 'training').toLowerCase()] || (u.purpose || 'training')}</td>
                    <td>{PROCESS_LABELS[(u.process || '').toLowerCase()] || u.process || '-'}</td>
                    <td>{u.status || 'active'}</td>
                    <td>
                      <button className="btn-edit" onClick={() => openEditModal(u)} style={{marginRight:'6px'}}>Edit</button>
                      <button className="btn-remove" onClick={() => handleRemoveUser(u.email)}>Remove</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="um-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="um-modal" onClick={e => e.stopPropagation()}>
              <div className="um-modal-header">
                <h3>Add New Candidate</h3>
                <button className="um-modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <form onSubmit={handleAddUser} className="um-modal-form">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="candidate@bt.com" required />
                </div>
                <div className="form-group">
                  <label>Access Status</label>
                  <select value={newAccessStatus} onChange={e => setNewAccessStatus(e.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Purpose</label>
                  <select value={newPurpose} onChange={e => handleNewPurpose(e.target.value)} required>
                    <option value="" disabled>Select a purpose</option>
                    {PURPOSES.map(p => (<option key={p} value={p}>{PURPOSE_LABELS[p]}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Process</label>
                  <select value={newProcess} onChange={e => setNewProcess(e.target.value)} required disabled={!newPurpose}>
                    <option value="" disabled>{newPurpose ? 'Select a process' : 'Select a purpose first'}</option>
                    {processesForPurpose(newPurpose).map(p => (<option key={p} value={p}>{PROCESS_LABELS[p] || p}</option>))}
                  </select>
                </div>
                <div className="um-modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-add-submit">Add Candidate</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && editUser && (
          <div className="um-modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="um-modal" onClick={e => e.stopPropagation()}>
              <div className="um-modal-header">
                <h3>Edit Candidate: {editUser.email}</h3>
                <button className="um-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
              </div>
              <form onSubmit={handleUpdateUser} className="um-modal-form">
                <div className="form-group">
                  <label>Access Status</label>
                  <select value={editAccessStatus} onChange={e => setEditAccessStatus(e.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Purpose</label>
                  <select value={editPurpose} onChange={e => handleEditPurpose(e.target.value)} required>
                    <option value="" disabled>Select a purpose</option>
                    {PURPOSES.map(p => (<option key={p} value={p}>{PURPOSE_LABELS[p]}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Process</label>
                  <select value={editProcess} onChange={e => setEditProcess(e.target.value)} required disabled={!editPurpose}>
                    <option value="" disabled>{editPurpose ? 'Select a process' : 'Select a purpose first'}</option>
                    {processOptions(editPurpose, editProcess).map(p => (<option key={p} value={p}>{PROCESS_LABELS[p] || p}</option>))}
                  </select>
                </div>
                <div className="um-modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn-add-submit">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
